import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { CheckCircle2, ChevronDown, ClipboardList, FileCheck2, FileText, Loader2, RefreshCw } from 'lucide-react'
import { Modal } from '@/components/ui'
import {
  prepareNdtJobReport,
  publishNdtJobReport,
  type PreparedNdtJobReport,
} from '@/services/ndtJobReport.service'
import {
  fetchOpenJobs,
  setJobAssignmentStatus,
  type InspectionStatus,
  type Job,
  type JobAssignment,
  type JobEquipment,
  type JobType,
} from '@/services/jobs.service'
import { supabase } from '@/lib/supabase/client'
import { setNdtExitPath } from '@/utils/ndtNavigation'
import { pendingControlCards } from '@/services/jobControlCards.service'
import { removeJobImages } from '@/services/jobImages.service'
import JobStatusPicker from './JobStatusPicker'
import JobControlCardsModal from './JobControlCardsModal'
import JobControlCardsPreview from './JobControlCardsPreview'
import { DoneAssignmentRow, PendingAssignmentRow, equipmentLabel, formatDate } from './JobAssignmentRow'

const PdfPreview = lazy(() => import('@/components/ui/PdfPreview'))

type Scope = 'mine' | 'all'

const SOON_DAYS = 30
/** Bulk inspections fire one change per equipment; collapse them into one reload. */
const REALTIME_DEBOUNCE_MS = 400
const DAY_MS = 24 * 60 * 60 * 1000

// Labels and colours mirror the WLL desktop app's job overview.
const JOB_TYPE_LABELS: Record<JobType, string> = {
  periodic: 'Periodisk kontroll',
  '48_months': '48-måneder',
  ndt: 'NDT',
  service_other: 'Service / annet',
}

const dueClassName = (date: string | null) => {
  if (!date) return 'text-slate-400'
  const due = new Date(`${date.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(due.getTime())) return 'text-slate-400'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((due.getTime() - today.getTime()) / DAY_MS)
  if (diffDays < 0) return 'text-rose-600 font-semibold'
  if (diffDays <= SOON_DAYS) return 'text-amber-700 font-medium'
  return 'text-slate-500'
}

interface JobsPanelProps {
  currentUserId: string | null
  isAdmin: boolean
  /** Bump to reload, e.g. after an inspection was published. */
  refreshKey: number
  onOpenEquipment: (equipment: JobEquipment, job: Job) => void
}

const JobsPanel = ({ currentUserId, isAdmin, refreshKey, onOpenEquipment }: JobsPanelProps) => {
  const navigate = useNavigate()
  const [scopeChoice, setScopeChoice] = useState<Scope | null>(null)
  const scope: Scope = scopeChoice ?? 'mine'
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())
  const [cardsOpen, setCardsOpen] = useState(false)
  const [reportJobId, setReportJobId] = useState<string | null>(null)
  const [reportPreview, setReportPreview] = useState<{
    job: Job
    report: PreparedNdtJobReport
    url: string
    title: string
  } | null>(null)
  const [publishingReport, setPublishingReport] = useState(false)
  const pendingCards = useMemo(() => pendingControlCards(jobs, currentUserId), [jobs, currentUserId])
  const pendingCount = jobs.reduce((sum, job) => sum + job.assignments.filter((a) => a.status !== 'completed').length, 0)

  const load = useCallback(async () => {
    if (!currentUserId) return
    setLoading(true)
    try {
      const result = await fetchOpenJobs(scope === 'all' ? 'all' : { inspectorId: currentUserId })
      setJobs(result)
      setError(null)
      if (result.length === 1) setExpanded(new Set([result[0].id]))
    } catch (err) {
      console.error('Error loading jobs:', err)
      setError('Kunne ikke laste jobber')
    } finally {
      setLoading(false)
    }
  }, [currentUserId, scope])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  // Desktop control cards complete assignments through database triggers.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    const schedule = () => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void load(), REALTIME_DEBOUNCE_MS)
    }
    const channel = supabase
      .channel('dashboard-jobs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, schedule)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_assignments' }, schedule)
      .subscribe()
    return () => {
      if (timer) clearTimeout(timer)
      void supabase.removeChannel(channel)
    }
  }, [load])

  const toggleExpanded = (jobId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(jobId)) next.delete(jobId)
      else next.add(jobId)
      return next
    })

  const replaceAssignment = (jobId: string, next: JobAssignment) =>
    setJobs((prev) =>
      prev.map((job) =>
        job.id !== jobId
          ? job
          : { ...job, assignments: job.assignments.map((a) => (a.id === next.id ? next : a)) },
      ),
    )

  const handleStatusChange = async (
    job: Job,
    assignment: JobAssignment,
    status: InspectionStatus | null,
    remarks: string | null,
    images: string[],
  ) => {
    const equipment = assignment.equipment
    if (!equipment) return
    setSavingIds((prev) => new Set(prev).add(assignment.id))
    replaceAssignment(job.id, {
      ...assignment,
      status: status ? 'completed' : 'assigned',
      inspection_status: status,
      inspection_remarks: status ? remarks : null,
      inspection_images: status ? images : [],
      control_card_at: null,
      equipment: status ? { ...equipment, inspection_status: status } : equipment,
    })
    try {
      await setJobAssignmentStatus(assignment.id, equipment, status, remarks, images)
      void removeJobImages(assignment.inspection_images.filter((path) => !images.includes(path)))
    } catch (err) {
      console.error('Error updating assignment:', err)
      replaceAssignment(job.id, assignment)
      alert('Kunne ikke oppdatere status')
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev)
        next.delete(assignment.id)
        return next
      })
    }
  }

  const statusControl = (job: Job, assignment: JobAssignment, canTick: boolean) => (
    <JobStatusPicker
      assignmentId={assignment.id}
      value={assignment.inspection_status ?? assignment.equipment?.inspection_status ?? null}
      remarks={assignment.inspection_remarks}
      images={assignment.inspection_images}
      done={assignment.status === 'completed'}
      equipmentLabel={equipmentLabel(assignment.equipment)}
      disabled={!canTick || !assignment.equipment}
      saving={savingIds.has(assignment.id)}
      onChange={(status, remarks, images) => handleStatusChange(job, assignment, status, remarks, images)}
    />
  )

  const handleCreateReport = async (job: Job) => {
    setReportJobId(job.id)
    try {
      const report = await prepareNdtJobReport(job)
      setReportPreview({
        job,
        report,
        url: URL.createObjectURL(report.blob),
        title: `${report.reportId} · ${job.ndt_subtypes.join(', ')}`,
      })
    } catch (err) {
      console.error('Error generating NDT job report:', err)
      alert(err instanceof Error ? err.message : 'Kunne ikke generere rapport')
    } finally {
      setReportJobId(null)
    }
  }

  const closeReportPreview = () => {
    if (reportPreview) URL.revokeObjectURL(reportPreview.url)
    setReportPreview(null)
  }

  const handlePublishReport = async () => {
    if (!reportPreview) return
    setPublishingReport(true)
    try {
      const published = await publishNdtJobReport(reportPreview.job, reportPreview.report)
      closeReportPreview()
      void load()
      alert(`Rapport ${published.reportId} publisert`)
    } catch (err) {
      console.error('Error publishing NDT job report:', err)
      alert(err instanceof Error ? err.message : 'Kunne ikke publisere rapport')
    } finally {
      setPublishingReport(false)
    }
  }

  const openJobReport = async (filePath: string) => {
    const { data, error } = await supabase.storage.from('reports').createSignedUrl(filePath, 60)
    if (error || !data?.signedUrl) {
      alert('Kunne ikke åpne rapport')
      return
    }
    window.open(data.signedUrl, '_blank')
  }

  const handleOpen = (job: Job, assignment: JobAssignment) => {
    if (!assignment.equipment) return
    sessionStorage.setItem('selectedCompanyId', assignment.equipment.company_id)

    if (assignment.work_type === 'ndt') {
      sessionStorage.setItem('inspectionType', 'ndt')
      navigate('/ndt-selection')
      return
    }
    if (assignment.work_type === 'service') {
      sessionStorage.setItem('inspectionType', 'ndt')
      sessionStorage.setItem('ndtType', 'SR')
      setNdtExitPath('/dashboard')
      navigate('/service-report')
      return
    }
    onOpenEquipment(assignment.equipment, job)
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <div className="px-5 py-3 shrink-0 border-b border-slate-200 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center shrink-0">
          <ClipboardList size={20} className="text-[#1E3A5F]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-slate-800 leading-tight">
            {scope === 'mine' ? 'Mine jobber' : 'Alle jobber'}
          </h2>
          <p className="text-sm text-slate-500">
            {loading && jobs.length === 0
              ? 'Laster...'
              : `${jobs.length} ${jobs.length === 1 ? 'jobb' : 'jobber'} · ${pendingCount} utstyr gjenstår`}
          </p>
        </div>
        <div className="flex rounded-lg bg-slate-100 p-1" role="group" aria-label="Vis jobber">
          {(['mine', 'all'] as const).map((option) => (
            <button
              key={option}
              onClick={() => setScopeChoice(option)}
              className={clsx(
                'px-4 h-10 rounded-md text-sm font-semibold transition-colors',
                scope === option ? 'bg-white text-[#1E3A5F] shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
              aria-pressed={scope === option}
            >
              {option === 'mine' ? 'Mine' : 'Alle'}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="h-12 w-12 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
          title="Oppdater"
          aria-label="Oppdater"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : undefined} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && jobs.length === 0 ? (
          <div className="flex items-center justify-center h-full py-8">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full py-8">
            <p className="text-sm text-rose-600">{error}</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12 gap-3 text-center">
            <ClipboardList size={36} className="text-slate-300" />
            <p className="text-base font-medium text-slate-600">
              {scope === 'mine' ? 'Du har ingen aktive jobber' : 'Ingen aktive jobber'}
            </p>
            {scope === 'mine' && (
              <button
                onClick={() => setScopeChoice('all')}
                className="h-11 px-4 rounded-md text-sm font-semibold text-[#1E3A5F] bg-[#1E3A5F]/10 hover:bg-[#1E3A5F]/15"
              >
                Vis alle jobber
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {jobs.map((job) => {
              const isOpen = expanded.has(job.id)
              const total = job.assignments.length
              const done = job.assignments.filter((a) => a.status === 'completed').length
              const percent = total > 0 ? Math.round((done / total) * 100) : 0
              const canTick = isAdmin || job.inspector_id === currentUserId
              const pending = job.assignments.filter((a) => a.status !== 'completed')
              const finished = job.assignments.filter((a) => a.status === 'completed')
              const due = formatDate(job.due_date)
              const details = [
                JOB_TYPE_LABELS[job.job_type] ?? job.job_type,
                job.order_number,
                job.location,
                scope === 'all' ? job.inspector_name : null,
              ].filter(Boolean)
              const ndtMethods = job.job_type === 'ndt' ? job.ndt_subtypes : []

              return (
                <li key={job.id}>
                  <div
                    className={clsx(
                      'flex items-center border-l-4 transition-colors',
                      isOpen
                        ? 'sticky top-0 z-10 bg-white border-[#1E3A5F] shadow-sm'
                        : 'border-transparent hover:bg-slate-50',
                    )}
                  >
                  <button
                    onClick={() => toggleExpanded(job.id)}
                    className={clsx(
                      'flex-1 min-w-0 flex items-center gap-4 pl-5 py-3.5 text-left min-h-[72px]',
                      ndtMethods.length > 0 ? 'pr-3' : 'pr-5',
                    )}
                    aria-expanded={isOpen}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-semibold text-slate-800 truncate">
                        {job.company_name ?? 'Ukjent kunde'}
                      </p>
                      <p className="text-sm text-slate-500 truncate">{details.join(' · ')}</p>
                      {job.description && (
                        <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">{job.description}</p>
                      )}
                      {ndtMethods.length > 0 && (
                        <ul className="mt-1.5 flex flex-wrap gap-1.5" aria-label="NDT-metoder og omfang">
                          {ndtMethods.map((method) => (
                            <li
                              key={method}
                              className="inline-flex items-center gap-1 rounded bg-[#1E3A5F]/10 px-2 py-0.5 text-xs"
                            >
                              <span className="font-semibold text-[#1E3A5F]">{method}</span>
                              {job.ndt_extents[method] && (
                                <span className="text-slate-600">{job.ndt_extents[method]}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <span className={clsx('text-sm whitespace-nowrap', dueClassName(job.due_date))}>
                        {due ? `Frist ${due}` : 'Ingen frist'}
                      </span>
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${percent}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{done}/{total} utført</span>
                      </div>
                    </div>
                    <ChevronDown
                      size={18}
                      className={clsx('shrink-0 text-slate-400 transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                  {ndtMethods.length > 0 && (
                    <button
                      onClick={() => void handleCreateReport(job)}
                      disabled={reportJobId !== null}
                      className="shrink-0 mr-5 h-11 px-4 inline-flex items-center gap-2 rounded-md text-sm font-semibold text-white bg-[#1E3A5F] hover:bg-[#16304f] transition-colors disabled:opacity-50"
                    >
                      {reportJobId === job.id ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                      Lag rapport
                    </button>
                  )}
                  </div>

                  {isOpen && (
                    <ul className="bg-slate-50/60 border-t border-slate-100 divide-y divide-slate-100">
                      {job.reports.length > 0 && (
                        <li className="flex flex-wrap items-center gap-2 pl-9 pr-5 py-2.5">
                          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Rapporter
                          </span>
                          {job.reports.map((report) => (
                            <button
                              key={report.id}
                              onClick={() => void openJobReport(report.file_path)}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-semibold text-[#1E3A5F] bg-[#1E3A5F]/10 hover:bg-[#1E3A5F]/15"
                            >
                              <FileText size={14} />
                              {report.order_number}
                            </button>
                          ))}
                        </li>
                      )}
                      {job.assignments.length === 0 && (
                        <li className="px-5 py-4 text-sm text-slate-400">Ingen utstyr tildelt</li>
                      )}
                      {pending.map((assignment) => (
                        <PendingAssignmentRow
                          key={assignment.id}
                          assignment={assignment}
                          onOpen={() => handleOpen(job, assignment)}
                          statusControl={statusControl(job, assignment, canTick)}
                        />
                      ))}
                      {finished.length > 0 && (
                        <li>
                          <p className="flex items-center gap-2 pl-9 pr-5 h-10 text-xs font-semibold uppercase tracking-widest text-slate-500 bg-slate-50">
                            <CheckCircle2 size={14} className="text-emerald-600" />
                            Utført ({finished.length})
                          </p>
                          <ul className="divide-y divide-slate-100 border-t border-slate-100">
                            {finished.map((assignment) => (
                              <DoneAssignmentRow
                                key={assignment.id}
                                assignment={assignment}
                                onOpen={() => handleOpen(job, assignment)}
                                statusControl={statusControl(job, assignment, canTick)}
                              />
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {scope === 'mine' && pendingCards.length > 0 && (
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-2.5 flex items-center gap-3">
          <FileCheck2 size={18} className="text-[#1E3A5F] shrink-0" />
          <p className="flex-1 text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{pendingCards.length}</span>{' '}
            {pendingCards.length === 1 ? 'kontrollkort klart' : 'kontrollkort klare'} til publisering
          </p>
          <JobControlCardsPreview cards={pendingCards} onGenerate={() => setCardsOpen(true)} />
          <button
            onClick={() => setCardsOpen(true)}
            className="h-11 px-4 rounded-md text-sm font-semibold text-white bg-[#1E3A5F] hover:bg-[#16304f] transition-colors"
          >
            Generer kontrollkort
          </button>
        </div>
      )}

      <JobControlCardsModal
        isOpen={cardsOpen}
        cards={pendingCards}
        onClose={() => setCardsOpen(false)}
        onPublished={load}
      />

      <Modal
        isOpen={!!reportPreview}
        onClose={closeReportPreview}
        title={reportPreview?.title ?? ''}
        size="xl"
        actions={(
          <button
            type="button"
            onClick={() => void handlePublishReport()}
            disabled={publishingReport}
            className="px-5 h-11 bg-[#1E3A5F] hover:bg-[#152C4A] text-white font-semibold text-sm rounded-md disabled:opacity-50"
          >
            {publishingReport ? 'Publiserer…' : 'Publiser'}
          </button>
        )}
      >
        {reportPreview && (
          <div className="min-h-[60vh]">
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Laster forhåndsvisning…
                </div>
              }
            >
              <PdfPreview src={reportPreview.url} title={reportPreview.title} />
            </Suspense>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default JobsPanel
